import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

const routes = [
    {
        path: '/',
        name: 'Home',
        component: () => import('../views/Home.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'Dashboard',
                component: () => import('../views/Dashboard.vue'),
                meta: { title: '仪表盘' }
            },
            {
                path: 'users',
                name: 'UserList',
                component: () => import('../views/UserList.vue'),
                meta: { title: '用户管理', permission: 'user:list' }
            },
            {
                path: 'roles',
                name: 'RoleList',
                component: () => import('../views/RoleList.vue'),
                meta: { title: '角色管理', permission: 'content:role' }
            },
            {
                path: 'voice',
                name: 'VoiceList',
                component: () => import('../views/VoiceList.vue'),
                meta: { title: '音色管理', permission: 'content:voice' }
            },
            {
                path: 'voice-design',
                name: 'VoiceDesignManage',
                component: () => import('../views/VoiceDesignManage.vue'),
                meta: { title: '音色设计', permission: 'content:voice-design' }
            },
            {
                path: 'function-group',
                name: 'FunctionGroupList',
                component: () => import('../views/FunctionGroupList.vue'),
                meta: { title: '功能管理', permission: 'device:function-group' }
            },
            {
                path: 'firmware',
                name: 'FirmwareList',
                component: () => import('@/views/FirmwareList.vue'),
                meta: { title: '固件管理', permission: 'device:firmware' }
            },
            {
                path: 'device',
                name: 'DeviceList',
                component: () => import('../views/DeviceList.vue'),
                meta: { title: '设备管理', permission: 'device:list' }
            },
            {
                path: 'audios',
                name: 'BreathAudioList',
                component: () => import('../views/BreathAudioList.vue'),
                meta: { title: '音频文件管理', permission: 'content:audio' }
            },
            {
                path: 'interactive-videos',
                name: 'InteractiveVideoList',
                component: () => import('../views/InteractiveVideoList.vue'),
                meta: { title: '互动视频', permission: 'content:audio' }
            },
            {
                path: 'flirt',
                name: 'FlirtList',
                component: () => import('../views/FlirtList.vue'),
                meta: { title: '意图管理', permission: 'content:flirt' }
            },
            {
                path: 'channels',
                name: 'DeviceChannelList',
                component: () => import('../views/DeviceChannelList.vue'),
                meta: { title: '渠道管理', permission: 'device:channel' }
            },
            {
                path: 'orders',
                name: 'OrderList',
                component: () => import('../views/OrderList.vue'),
                meta: { title: '订单管理', permission: 'business:order' }
            },
            {
                path: 'tasks',
                name: 'TaskList',
                component: () => import('../views/TaskList.vue'),
                meta: { title: '任务管理', permission: 'business:order' }
            },
            {
                path: 'tasks/:id',
                name: 'TaskDetail',
                component: () => import('../views/TaskDetail.vue'),
                meta: { title: '任务详情', permission: 'business:order' }
            },
            {
                path: 'task-workers',
                name: 'TaskWorkerList',
                component: () => import('../views/TaskWorkerList.vue'),
                meta: { title: 'Worker 管理', permission: 'business:task' }
            },
            // 系统管理模块（新增）
            {
                path: 'system/admins',
                name: 'AdminUserList',
                component: () => import('../views/system/AdminUserList.vue'),
                meta: { title: '管理员管理', permission: 'system:admin' }
            },
            {
                path: 'system/roles',
                name: 'AdminRoleList',
                component: () => import('../views/system/AdminRoleList.vue'),
                meta: { title: '角色权限', permission: 'system:role' }
            },
            {
                path: 'system/audit-logs',
                name: 'AuditLogList',
                component: () => import('../views/system/AuditLogList.vue'),
                meta: { title: '审计日志', permission: 'system:audit' }
            }
        ]
    },
    {
        path: '/region-select',
        name: 'RegionSelect',
        component: () => import('../views/RegionSelect.vue'),
        meta: { requiresAuth: true, title: '选择业务区域' }
    },
    {
        path: '/login',
        name: 'Login',
        component: () => import('../views/Login.vue'),
        meta: { requiresAuth: false }
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes
})

// 发布切换期间若命中旧 chunk，自动刷新一次获取最新入口与资源
// 使用 sessionStorage 防止页面刷新后变量重置导致循环刷新
const CHUNK_RETRY_FLAG = 'chunk_load_retry_done'
router.onError((error) => {
    const message = String((error && (error.message || error)) || '')
    const isChunkLoadError =
        message.includes('Loading chunk') ||
        message.includes('ChunkLoadError') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('ERR_HTTP2_PROTOCOL_ERROR')

    const hasRetriedChunkLoad = sessionStorage.getItem(CHUNK_RETRY_FLAG) === '1'
    if (isChunkLoadError && !hasRetriedChunkLoad) {
        sessionStorage.setItem(CHUNK_RETRY_FLAG, '1')
        window.location.reload()
    }
})

router.afterEach(() => {
    sessionStorage.removeItem(CHUNK_RETRY_FLAG)
})

const CN_ADMIN_ORIGIN = 'https://cms.luminiea.com'
const normalizeToken = (token) => (token ? String(token).replace(/^Bearer\s+/i, '').trim() : '')

router.beforeEach((to, from, next) => {
    const isUsHost = window.location.hostname.includes('admin-us.lingxidance.com')

    // 海外站：系统管理统一跳转国内后台（携带 token），避免在海外域名做 RBAC/审计等
    if (isUsHost && to.path.startsWith('/system')) {
        const token = normalizeToken(localStorage.getItem('admin_token'))
        if (!token) {
            window.location.href = `${CN_ADMIN_ORIGIN}/login`
            return
        }
        // hash 传 token，避免 URI 过长导致 414
        window.location.href = `${CN_ADMIN_ORIGIN}${to.fullPath}#token=${encodeURIComponent(token)}`
        return
    }

    // 海外域名不提供登录页，强制回国内登录入口
    if (to.path === '/login' && isUsHost) {
        window.location.href = `${CN_ADMIN_ORIGIN}/login`
        return
    }

    // 设置页面标题
    if (to.meta.title) {
        document.title = `${to.meta.title} - 后台管理系统`
    }
    
    // 认证检查
    if (to.matched.some(record => record.meta.requiresAuth)) {
        const isAuthenticated = store.getters.isAdminAuthenticated
        //console.log('路由守卫检查:', { path: to.path, isAuthenticated, token: !!store.state.adminToken })
        
        if (!isAuthenticated) {
            // 未登录：无论访问哪个域名，统一跳转到国内登录页
            if (isUsHost) {
                const target = `${CN_ADMIN_ORIGIN}/login`
                window.location.href = target
                return
            }
            next({ path: '/login', query: { redirect: to.fullPath } })
        } else {
            // 已登录但本地缺少用户上下文时，尝试回填 profile（跨域 token 注入场景常见）
            if (!store.state.adminUser) {
                store.dispatch('fetchAdminProfile').catch(() => {})
            }

            // 已登录但访问根路径：最佳体验是先进入「区域选择页」
            if (to.path === '/' && from.path === '/login') {
                next({ path: '/region-select' })
                return
            }
            // 权限检查
            const requiredPermission = to.meta.permission
            if (requiredPermission) {
                const hasPermission = store.getters.hasPermission(requiredPermission)
                if (!hasPermission) {
                    //console.log('无权限访问:', requiredPermission)
                    // 无权限，重定向到首页
                    next({ path: '/' })
                    return
                }
            }
            next()
        }
    } else {
        // 已登录用户访问登录页，重定向到首页
        if (store.getters.isAdminAuthenticated && to.path === '/login') {
            console.log('已登录，从登录页重定向到首页')
            next('/region-select')
        } else {
            next()
        }
    }
})

export default router
